/**
 * Pulsante "torna su" di 120''LAB (custom element `<lab-scroll-to-top>`).
 *
 * Il markup e il CSS sono in `sections/lab-scroll-to-top.liquid`. Questo modulo:
 * - mostra il pulsante (toglie `hidden`) quando la pagina e scorsa piu di `data-display-after`
 *   px; con 0 il pulsante e sempre visibile;
 * - al click riporta la pagina in cima con scorrimento fluido;
 * - legge lo scorrimento sia di `window` sia di `.page-wrapper`: da 990px Horizon scorre
 *   `.page-wrapper` (html e body hanno `overflow: hidden`), sotto scorre il documento.
 *   Il valore usato e il maggiore dei due, il click chiama `scrollTo` su entrambi;
 * - nella scheda prodotto alza il pulsante dell'altezza della barra sticky "aggiungi al
 *   carrello" di Horizon quando e visibile (`--lab-stt-sticky-offset`);
 * - compensa la larghezza della barra di scorrimento di `.page-wrapper` (`--lab-stt-scrollbar`),
 *   perche `position: fixed` misura dal bordo del viewport e non dal bordo della barra.
 */

const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
const STICKY_BAR_SELECTOR = '.sticky-add-to-cart__bar';
const BUTTON_SELECTOR = '.lab-scroll-to-top__button';
const DEFAULT_DISPLAY_AFTER = 400;

class LabScrollToTop extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {Element | null} */
  #pageWrapper = null;

  /** @type {Element | null} */
  #stickyBar = null;

  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /** @type {MutationObserver | null} */
  #stickyMutationObserver = null;

  /** @type {ResizeObserver | null} */
  #wrapperResizeObserver = null;

  /** @type {number} */
  #displayAfter = DEFAULT_DISPLAY_AFTER;

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #stickyOffset = 0;

  /** @type {number} */
  #scrollbarWidth = 0;

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true });
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#stickyResizeObserver?.disconnect();
    this.#stickyMutationObserver?.disconnect();
    this.#wrapperResizeObserver?.disconnect();
    this.#stickyResizeObserver = null;
    this.#stickyMutationObserver = null;
    this.#wrapperResizeObserver = null;
    this.#stickyBar = null;

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
  }

  #init() {
    // L'elemento potrebbe essere stato rimosso prima di DOMContentLoaded
    if (!this.isConnected || this.#abortController) return;

    this.#button = this.querySelector(BUTTON_SELECTOR);
    if (!this.#button) return;

    const displayAfter = Number.parseInt(this.dataset.displayAfter ?? '', 10);
    this.#displayAfter = Number.isFinite(displayAfter) && displayAfter >= 0 ? displayAfter : DEFAULT_DISPLAY_AFTER;

    this.#pageWrapper = document.querySelector(PAGE_WRAPPER_SELECTOR);
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('pageshow', this.#scheduleUpdate, { signal });
    this.#pageWrapper?.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    this.#button.addEventListener('click', this.#scrollToTop, { signal });

    // La barra di scorrimento di `.page-wrapper` compare/scompare con il contenuto e con il breakpoint
    if (this.#pageWrapper && 'ResizeObserver' in window) {
      this.#wrapperResizeObserver = new ResizeObserver(this.#scheduleUpdate);
      this.#wrapperResizeObserver.observe(this.#pageWrapper);
    }

    // Se la sezione viene ri-renderizzata dall'editor la barra sticky potrebbe essere cambiata
    document.addEventListener('shopify:section:load', this.#scheduleUpdate, { signal });

    this.#update();
  }

  /**
   * Scorrimento corrente della pagina: il maggiore tra `window` e `.page-wrapper`.
   * @returns {number}
   */
  #getScrollTop() {
    return Math.max(window.scrollY, this.#pageWrapper?.scrollTop ?? 0);
  }

  /**
   * Raggruppa piu eventi nello stesso frame.
   */
  #scheduleUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    if (!this.#button) return;

    const visible = this.#displayAfter === 0 || this.#getScrollTop() > this.#displayAfter;
    if (this.#button.hidden === visible) this.#button.hidden = !visible;

    this.#syncScrollbarWidth();
    this.#syncStickyOffset();
  }

  /**
   * Larghezza della barra di scorrimento di `.page-wrapper` (0 sotto i 990px, dove non scorre).
   */
  #syncScrollbarWidth() {
    const wrapper = this.#pageWrapper;
    const width = wrapper ? Math.max(0, wrapper.offsetWidth - wrapper.clientWidth) : 0;
    if (width === this.#scrollbarWidth) return;

    this.#scrollbarWidth = width;
    this.style.setProperty('--lab-stt-scrollbar', `${width}px`);
  }

  /**
   * Altezza della barra sticky "aggiungi al carrello" di Horizon, solo quando e visibile
   * (`data-stuck="true"`). Se la barra non c'e (altre pagine) o viene sostituita da un
   * re-render, l'osservazione si aggiorna da sola.
   */
  #syncStickyOffset() {
    const bar = document.querySelector(STICKY_BAR_SELECTOR);
    if (bar !== this.#stickyBar) this.#observeStickyBar(bar);

    const height = bar instanceof HTMLElement && bar.dataset.stuck === 'true' ? bar.offsetHeight : 0;
    if (height === this.#stickyOffset) return;

    this.#stickyOffset = height;
    this.style.setProperty('--lab-stt-sticky-offset', `${height}px`);
  }

  /**
   * @param {Element | null} bar
   */
  #observeStickyBar(bar) {
    this.#stickyResizeObserver?.disconnect();
    this.#stickyMutationObserver?.disconnect();
    this.#stickyBar = bar;

    if (!bar) return;

    if ('ResizeObserver' in window) {
      this.#stickyResizeObserver = new ResizeObserver(this.#scheduleUpdate);
      this.#stickyResizeObserver.observe(bar);
    }

    this.#stickyMutationObserver = new MutationObserver(this.#scheduleUpdate);
    this.#stickyMutationObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  #scrollToTop = () => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /** @type {ScrollToOptions} */
    const options = { top: 0, behavior: reducedMotion ? 'instant' : 'smooth' };

    window.scrollTo(options);
    this.#pageWrapper?.scrollTo(options);
  };
}

if (!customElements.get('lab-scroll-to-top')) {
  customElements.define('lab-scroll-to-top', LabScrollToTop);
}
